/**
 * Polyline Drawing Manager
 * google.maps.drawing.DrawingManager の廃止のため代替実装（Polyline用）
 *
 * クリックで点を追加、ダブルクリックで描画完了（最低2点必要）
 * setDrawingMode(null) または endDrawingManager() で描画を停止
 */

// google.maps.event互換のイベント管理を提供するシンボル
const EVENT_HANDLERS_KEY = Symbol("eventHandlers");

/**
 * カスタムオーバーレイでクリックを検知
 * マップ全体を覆う透明なdiv要素でクリック・ダブルクリック・マウス移動を検知
 *
 * @param map - Google Mapsインスタンス
 * @param clickHandler - クリック時のコールバック（緯度経度を渡す）
 * @param dblClickHandler - ダブルクリック時のコールバック（緯度経度を渡す）
 * @param mouseMoveHandler - マウス移動時のコールバック（緯度経度を渡す）
 * @returns OverlayViewインスタンス
 */
function createDrawingOverlay(
    map: google.maps.Map,
    clickHandler: (latLng: google.maps.LatLng) => void,
    dblClickHandler: (latLng: google.maps.LatLng) => void,
    mouseMoveHandler: (latLng: google.maps.LatLng) => void
): google.maps.OverlayView {
    /**
     * 描画用カスタムオーバーレイクラス
     * マップ全体を覆う透明なdiv要素でイベントを検知する
     */
    class DrawingOverlay extends google.maps.OverlayView {
        // マップ全体を覆う透明なdiv要素
        private div: HTMLDivElement | null = null;
        // クリックイベントハンドラー（マウス座標→緯度経度変換を含む）
        private clickHandlerFn: ((e: MouseEvent) => void) | null = null;
        // ダブルクリックイベントハンドラー（マウス座標→緯度経度変換を含む）
        private dblClickHandlerFn: ((e: MouseEvent) => void) | null = null;
        // マウス移動イベントハンドラー（マウス座標→緯度経度変換を含む）
        private mouseMoveHandlerFn: ((e: MouseEvent) => void) | null = null;

        constructor() {
            super();
            // オーバーレイをマップに追加
            this.setMap(map);

            // クリックイベントハンドラーを定義
            // マウス座標を緯度経度に変換してコールバックを呼ぶ
            this.clickHandlerFn = (e: MouseEvent) => {
                e.stopPropagation(); // イベントの伝播を停止
                e.preventDefault(); // デフォルト動作を防止
                const projection = this.getProjection(); // 座標変換用のprojectionを取得
                if (projection) {
                    // div要素の位置を取得
                    const bounds = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                    // クリック位置をdiv内の相対座標に変換
                    const point = new google.maps.Point(
                        e.clientX - bounds.left,
                        e.clientY - bounds.top
                    );
                    // ピクセル座標を緯度経度に変換
                    const latLng = projection.fromContainerPixelToLatLng(point);
                    if (latLng) {
                        // 変換した緯度経度でコールバックを呼ぶ
                        clickHandler(latLng);
                    }
                }
            };

            // ダブルクリックイベントハンドラーを定義
            // 処理内容はクリックと同じ
            this.dblClickHandlerFn = (e: MouseEvent) => {
                e.stopPropagation();
                e.preventDefault();
                const projection = this.getProjection();
                if (projection) {
                    const bounds = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                    const point = new google.maps.Point(
                        e.clientX - bounds.left,
                        e.clientY - bounds.top
                    );
                    const latLng = projection.fromContainerPixelToLatLng(point);
                    if (latLng) {
                        dblClickHandler(latLng);
                    }
                }
            };

            // マウス移動イベントハンドラーを定義
            // stopPropagation/preventDefaultは不要（移動は頻繁に発生するため）
            this.mouseMoveHandlerFn = (e: MouseEvent) => {
                const projection = this.getProjection();
                if (projection) {
                    const bounds = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                    // マウス位置をdiv内の相対座標に変換
                    const point = new google.maps.Point(
                        e.clientX - bounds.left,
                        e.clientY - bounds.top
                    );
                    // ピクセル座標を緯度経度に変換
                    const latLng = projection.fromContainerPixelToLatLng(point);
                    if (latLng) {
                        // 変換した緯度経度でコールバックを呼ぶ（プレビュー線の更新用）
                        mouseMoveHandler(latLng);
                    }
                }
            };
        }

        /**
         * オーバーレイがマップに追加されたときに呼ばれる
         * div要素を作成してイベントリスナーを登録
         */
        onAdd() {
            // 透明なdiv要素を作成
            this.div = document.createElement("div");
            this.div.style.position = "absolute";
            this.div.style.cursor = "crosshair"; // カーソルを十字に変更
            this.div.style.backgroundColor = "rgba(255, 0, 0, 0.01)"; // ほぼ透明（完全透明だとイベントが取れない）
            this.div.style.pointerEvents = "auto"; // クリックイベントを受け取る

            // 各イベントリスナーをdiv要素に登録
            if (this.clickHandlerFn) {
                this.div.addEventListener("click", this.clickHandlerFn);
            }
            if (this.dblClickHandlerFn) {
                this.div.addEventListener("dblclick", this.dblClickHandlerFn);
            }
            if (this.mouseMoveHandlerFn) {
                this.div.addEventListener("mousemove", this.mouseMoveHandlerFn);
            }

            // マップのペイン（レイヤー）を取得
            const panes = this.getPanes();
            if (panes) {
                // floatPane を使用（マーカーより上位のレイヤーに配置）
                panes.floatPane.appendChild(this.div);
            }
        }

        /**
         * マップの描画が更新されるたびに呼ばれる
         * div要素のサイズと位置をマップ全体に合わせて更新
         */
        draw() {
            if (this.div) {
                const projection = this.getProjection(); // 座標変換用
                const map = this.getMap() as google.maps.Map;
                if (projection && map) {
                    // 現在のマップの表示範囲を取得
                    const bounds = map.getBounds();
                    if (bounds) {
                        // 北東と南西の角をピクセル座標に変換
                        const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast());
                        const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest());

                        if (ne && sw) {
                            // div要素をマップ全体をカバーするように配置・サイズ調整
                            this.div.style.left = sw.x + "px";
                            this.div.style.top = ne.y + "px";
                            this.div.style.width = (ne.x - sw.x) + "px";
                            this.div.style.height = (sw.y - ne.y) + "px";
                        }
                    }
                }
            }
        }

        /**
         * オーバーレイがマップから削除されるときに呼ばれる
         * イベントリスナーを削除してクリーンアップ
         */
        onRemove() {
            if (this.div) {
                // 登録したイベントリスナーをすべて削除
                if (this.clickHandlerFn) {
                    this.div.removeEventListener("click", this.clickHandlerFn);
                }
                if (this.dblClickHandlerFn) {
                    this.div.removeEventListener("dblclick", this.dblClickHandlerFn);
                }
                if (this.mouseMoveHandlerFn) {
                    this.div.removeEventListener("mousemove", this.mouseMoveHandlerFn);
                }
                // div要素をDOMから削除
                this.div.parentNode?.removeChild(this.div);
                this.div = null;
            }
        }
    }

    // インスタンスを作成して返す
    return new DrawingOverlay();
}

/**
 * Polyline描画マネージャー
 * google.maps.drawing.DrawingManager の廃止に伴う代替実装
 *
 * 使用方法:
 * 1. new LineDrawingManager({ map, drawingMode: "polyline", ... }) で作成
 * 2. クリックで点を追加、ダブルクリックで描画完了
 * 3. polylinecomplete イベントで完成したpolylineを取得
 * 4. setDrawingMode(null) で描画停止
 */
export class LineDrawingManager {
    private map: google.maps.Map | null = null;
    private polyline: google.maps.Polyline | null = null;
    private polylineOptions: google.maps.PolylineOptions;
    private path: google.maps.LatLng[] = [];
    private listeners: { [key: string]: google.maps.MapsEventListener[] } = {};
    private drawingOverlay: google.maps.OverlayView | null = null;
    private isDrawing: boolean = false;
    private cursorPolyline: google.maps.Polyline | null = null; // カーソルまでのプレビュー線
    [EVENT_HANDLERS_KEY]: { [key: string]: ((arg: any) => void)[] } = {};

    constructor(options: {
        map?: google.maps.Map;
        polylineOptions?: google.maps.PolylineOptions;
        drawingMode?: string | null;
        drawingControl?: boolean;
    }) {
        this.map = options.map || null;
        this.polylineOptions = options.polylineOptions || {
            strokeColor: "#FF0000",
            strokeOpacity: 1.0,
            strokeWeight: 2,
            editable: true
        };

        // drawingModeが指定されている場合は描画を開始
        if (this.map && options.drawingMode) {
            this.startDrawing();
        }
    }

    /**
     * 描画モードを設定
     *
     * @param mode - "polyline" で描画開始、null で描画停止
     */
    public setDrawingMode(mode: string | null) {
        if (mode === "polyline") {
            this.startDrawing();
        } else {
            this.stopDrawing();
        }
    }

    /**
     * マップを設定
     *
     * @param map - Google Mapsインスタンス、null でマップから削除
     */
    public setMap(map: google.maps.Map | null) {
        if (this.isDrawing) {
            this.stopDrawing();
        }
        this.map = map;
    }

    /**
     * 描画を開始
     */
    private startDrawing() {
        if (!this.map || this.isDrawing) return;

        this.isDrawing = true;
        this.path = [];

        // マップのドラッグとズームを無効化
        this.map.setOptions({
            draggable: false,
            disableDoubleClickZoom: true
        });

        // カスタムオーバーレイを使用してクリックを検知
        this.drawingOverlay = createDrawingOverlay(
            this.map,
            (latLng: google.maps.LatLng) => {
                this.addPoint(latLng);
            },
            (latLng: google.maps.LatLng) => {
                this.completeDrawing();
            },
            (latLng: google.maps.LatLng) => {
                this.updateCursorPolyline(latLng);
            }
        );
    }

    /**
     * 描画を停止
     */
    private stopDrawing() {
        if (!this.isDrawing) return;

        this.isDrawing = false;

        // マップのオプションを元に戻す
        if (this.map) {
            this.map.setOptions({
                draggable: true,
                disableDoubleClickZoom: false
            });
        }

        // オーバーレイを削除
        if (this.drawingOverlay) {
            this.drawingOverlay.setMap(null);
            this.drawingOverlay = null;
        }

        // プレビュー線を削除
        if (this.cursorPolyline) {
            this.cursorPolyline.setMap(null);
            this.cursorPolyline = null;
        }

        // 描画中のポリラインを削除
        if (this.polyline && this.polyline.getPath().getLength() === 0) {
            this.polyline.setMap(null);
            this.polyline = null;
        }

        this.path = [];
    }

    /**
     * カーソル位置までのプレビュー線を更新
     * マウス移動時に最後の点からカーソル位置までの線をプレビュー表示
     *
     * @param latLng - カーソル位置の緯度経度
     */
    private updateCursorPolyline(latLng: google.maps.LatLng) {
        if (this.path.length === 0) return;

        const previewPath = [...this.path, latLng];

        if (!this.cursorPolyline) {
            // プレビュー線を作成
            this.cursorPolyline = new google.maps.Polyline({
                strokeColor: this.polylineOptions.strokeColor,
                strokeOpacity: this.polylineOptions.strokeOpacity || 0.6,
                strokeWeight: this.polylineOptions.strokeWeight,
                map: this.map,
                path: previewPath,
                clickable: false
            });
        } else {
            // 既存のプレビュー線のパスを更新
            this.cursorPolyline.setPath(previewPath);
        }
    }

    /**
     * 点を追加
     * クリックされた位置にポイントを追加し、polylineを更新
     *
     * @param latLng - 追加する点の緯度経度
     */
    private addPoint(latLng: google.maps.LatLng) {
        this.path.push(latLng);

        if (!this.polyline) {
            // 新しいポリラインを作成
            this.polyline = new google.maps.Polyline({
                ...this.polylineOptions,
                map: this.map,
                path: this.path
            });
        } else {
            // 既存のポリラインに点を追加
            this.polyline.getPath().push(latLng);
        }

        // プレビュー線をリセット（次のポイントまでのプレビューを表示するため）
        if (this.cursorPolyline) {
            this.cursorPolyline.setMap(null);
            this.cursorPolyline = null;
        }
    }

    /**
     * 描画完了
     * ダブルクリック時に呼ばれ、polylinecomplete イベントを発火
     * 最低2点必要、不足の場合は描画をキャンセル
     */
    private completeDrawing() {
        if (!this.polyline || this.path.length < 2) {
            // 最低2点必要
            if (this.polyline) {
                this.polyline.setMap(null);
                this.polyline = null;
            }
            this.stopDrawing();
            return;
        }

        const completedPolyline = this.polyline;

        // 描画モードを停止
        this.stopDrawing();

        // polylinecompleteイベントを発火
        this.trigger("polylinecomplete", completedPolyline);

        // overlaycompleteイベントを発火
        this.trigger("overlaycomplete", {
            type: "polyline",
            overlay: completedPolyline
        });

        this.polyline = null;
    }

    /**
     * 描画を強制完成（仮確定用）
     * 外部から呼び出して現在の描画を強制的に完成させる
     */
    public forceComplete() {
        this.completeDrawing();
    }

    /**
     * イベントリスナーを追加
     *
     * @param eventName - イベント名（"polylinecomplete", "overlaycomplete"）
     * @param handler - イベントハンドラー関数
     * @returns リスナーオブジェクト（remove()で削除可能）
     */
    public addListener(eventName: string, handler: (arg: any) => void): google.maps.MapsEventListener {
        if (!this[EVENT_HANDLERS_KEY][eventName]) {
            this[EVENT_HANDLERS_KEY][eventName] = [];
        }
        this[EVENT_HANDLERS_KEY][eventName].push(handler);

        // google.maps.MapsEventListener互換のオブジェクトを返す
        return {
            remove: () => {
                const handlers = this[EVENT_HANDLERS_KEY][eventName];
                if (handlers) {
                    const index = handlers.indexOf(handler);
                    if (index > -1) {
                        handlers.splice(index, 1);
                    }
                }
            }
        } as google.maps.MapsEventListener;
    }

    /**
     * イベントを発火
     * 登録されたすべてのハンドラーを呼び出す
     *
     * @param eventName - イベント名
     * @param arg - ハンドラーに渡す引数
     */
    private trigger(eventName: string, arg: any) {
        const handlers = this[EVENT_HANDLERS_KEY][eventName];
        if (handlers) {
            handlers.forEach(handler => handler(arg));
        }
    }

    /**
     * 特定のイベントのリスナーをすべてクリア
     *
     * @param eventName - クリアするイベント名
     */
    public clearListeners(eventName: string) {
        if (this[EVENT_HANDLERS_KEY][eventName]) {
            this[EVENT_HANDLERS_KEY][eventName] = [];
        }
    }

    /**
     * すべてのイベントリスナーをクリア
     */
    public clearAllListeners() {
        this[EVENT_HANDLERS_KEY] = {};
    }

    /**
     * クリーンアップ
     * すべてのリソースを解放し、マップから削除
     */
    public destroy() {
        this.stopDrawing();
        this.clearAllListeners();
        if (this.polyline) {
            this.polyline.setMap(null);
            this.polyline = null;
        }
        if (this.cursorPolyline) {
            this.cursorPolyline.setMap(null);
            this.cursorPolyline = null;
        }
        if (this.drawingOverlay) {
            this.drawingOverlay.setMap(null);
            this.drawingOverlay = null;
        }
        this.map = null;
    }
}

/**
 * google.maps.event互換のヘルパー関数
 * LineDrawingManagerにイベントリスナーを追加
 *
 * @param instance - LineDrawingManagerインスタンス
 * @param eventName - イベント名
 * @param handler - イベントハンドラー関数
 * @returns リスナーオブジェクト
 */
export const addLineDrawingListener = (
    instance: LineDrawingManager,
    eventName: string,
    handler: (arg: any) => void
): google.maps.MapsEventListener => {
    return instance.addListener(eventName, handler);
};

/**
 * google.maps.event互換のヘルパー関数
 * LineDrawingManagerの特定イベントのリスナーをすべてクリア
 *
 * @param instance - LineDrawingManagerインスタンス
 * @param eventName - クリアするイベント名
 */
export const clearLineDrawingListeners = (
    instance: LineDrawingManager,
    eventName: string
) => {
    instance.clearListeners(eventName);
};
